import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useBaseUrl } from '../../../utils/utils';
import * as localforage from 'localforage';
import Swal from 'sweetalert2';

interface ResourceFile {
    id: string;
    title: string;
    summary: string;
    category_name: string;
    category_icon: string;
    file_type: string;
    formatted_file_size: string;
    author: string;
    published_date: string;
    download_count: number;
    view_count: number;
    average_rating: number;
    tags: string[];
    privacy_level: string;
    version: string;
    can_download: boolean;
}

interface ResourceCategory {
    id: string;
    name: string;
    icon: string;
    color: string;
    resource_count: number;
}

interface Props {
    onBack: () => void;
}

const DownloadsCenter: React.FC<Props> = ({ onBack }) => {
    const baseUrl = useBaseUrl();
    const [resources, setResources] = useState<ResourceFile[]>([]);
    const [categories, setCategories] = useState<ResourceCategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [selectedFileType, setSelectedFileType] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage] = useState<number>(12);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('title');

    const fileTypeIcons = {
        pdf: { icon: 'bx-file-pdf', color: '#dc3545' },
        doc: { icon: 'bx-file-doc', color: '#0d6efd' },
        xls: { icon: 'bx-table', color: '#198754' },
        ppt: { icon: 'bx-slideshow', color: '#fd7e14' },
        img: { icon: 'bx-image', color: '#6f42c1' },
        video: { icon: 'bx-video', color: '#d63384' },
        audio: { icon: 'bx-music', color: '#20c997' },
        zip: { icon: 'bx-archive', color: '#6c757d' },
        other: { icon: 'bx-file', color: '#495057' }
    };

    const fileTypes = Object.keys(fileTypeIcons);

    useEffect(() => {
        loadCategories();
        loadResources();
    }, []);

    useEffect(() => {
        loadResources();
    }, [selectedCategory, selectedFileType, sortBy]);

    const loadCategories = async () => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/categories/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && Array.isArray(response.data)) {
                setCategories(response.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            setCategories([]); // No hardcoded data
        }
    };

    const loadResources = async () => {
        setLoading(true);
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            let url = `${baseUrl}/resources/api/v1/library/`;
            const params = new URLSearchParams();
            
            if (selectedCategory !== 'all') params.append('category', selectedCategory);
            if (selectedFileType !== 'all') params.append('file_type', selectedFileType);
            if (sortBy) params.append('ordering', sortBy);

            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                if (Array.isArray(response.data.results)) {
                    // Paginated response
                    setResources(currentPage === 1 ? response.data.results : [...resources, ...response.data.results]);
                    setTotalCount(response.data.count || 0);
                    setHasMore(!!response.data.next);
                } else if (Array.isArray(response.data)) {
                    // Simple array response
                    setResources(response.data);
                    setTotalCount(response.data.length);
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error loading resources:', error);
            setResources([]); // No hardcoded data
        } finally {
            setLoading(false);
        }
    };

    const loadMoreResources = async () => {
        if (!hasMore || loading) return;
        
        setCurrentPage(prev => prev + 1);
        await loadResources();
    };

    const handleScroll = () => {
        if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight) return;
        loadMoreResources();
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore, loading]);

    const downloadResource = async (resource: ResourceFile) => {
        if (!resource.can_download) {
            Swal.fire('Access Denied', 'You do not have permission to download this resource.', 'error');
            return;
        }

        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            const response = await axios.get(`${baseUrl}/resources/api/v1/library/${resource.id}/download/`, {
                headers: {
                    'X-Clinic-ID': clinicId || '',
                    'X-Staff-ID': staffId || ''
                },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${resource.title}.${resource.file_type}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Update download count locally
            setResources(prev => prev.map(r => 
                r.id === resource.id 
                    ? { ...r, download_count: r.download_count + 1 }
                    : r
            ));

            Swal.fire('Success', 'Resource downloaded successfully!', 'success');
        } catch (error) {
            console.error('Download error:', error);
            Swal.fire('Error', 'Failed to download resource. Please try again.', 'error');
        }
    };

    const rateResource = async (resourceId: string, rating: number) => {
        try {
            const clinicId = await localforage.getItem('clinic_id') as string;
            const staffId = await localforage.getItem('staff_id') as string;

            await axios.post(`${baseUrl}/resources/api/v1/library/${resourceId}/rate/`, 
                { rating },
                {
                    headers: {
                        'X-Clinic-ID': clinicId || '',
                        'X-Staff-ID': staffId || '',
                        'Content-Type': 'application/json'
                    }
                }
            );

            Swal.fire('Thank you!', 'Your rating has been submitted.', 'success');
            loadResources(); // Refresh to get updated rating
        } catch (error) {
            console.error('Rating error:', error);
            Swal.fire('Error', 'Failed to submit rating. Please try again.', 'error');
        }
    };

    const filteredResources = resources.filter(resource =>
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getFileTypeIcon = (fileType: string) => {
        const type = fileTypeIcons[fileType as keyof typeof fileTypeIcons] || fileTypeIcons.other;
        return type;
    };

    const renderStars = (rating: number, onRate?: (rating: number) => void) => {
        return (
            <div className="d-flex">
                {[1, 2, 3, 4, 5].map((star) => (
                    <i
                        key={star}
                        className={`bx ${star <= rating ? 'bxs-star' : 'bx-star'} ${onRate ? 'cursor-pointer' : ''}`}
                        style={{ color: star <= rating ? '#ffc107' : '#e9ecef', fontSize: '14px' }}
                        onClick={() => onRate && onRate(star)}
                    ></i>
                ))}
            </div>
        );
    };

    return (
        <div className="container-fluid p-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">
                                <i className="bx bx-download me-2 text-primary"></i>
                                Downloads Center
                            </h4>
                            <p className="text-muted mb-0">Access and download medical resources, forms, and guidelines</p>
                        </div>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={onBack}
                        >
                            <i className="bx bx-arrow-back me-2"></i>
                            Back to Resources
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <div className="row g-3 align-items-end">
                                <div className="col-md-4">
                                    <label className="form-label small text-muted">Search Resources</label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="bx bx-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search by title, author, or tags..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small text-muted">Category</label>
                                    <select
                                        className="form-select"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        <option value="all">All Categories</option>
                                        {categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                                {category.name} ({category.resource_count})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small text-muted">File Type</label>
                                    <select
                                        className="form-select"
                                        value={selectedFileType}
                                        onChange={(e) => setSelectedFileType(e.target.value)}
                                    >
                                        <option value="all">All Types</option>
                                        {fileTypes.map((type) => (
                                            <option key={type} value={type}>
                                                {type.toUpperCase()}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small text-muted">Sort By</label>
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="title">Title</option>
                                        <option value="-created_at">Newest</option>
                                        <option value="-download_count">Most Downloaded</option>
                                        <option value="-view_count">Most Viewed</option>
                                    </select>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resources Grid/List */}
            <div className="row">
                <div className="col-12">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading resources...</p>
                        </div>
                    ) : filteredResources.length > 0 ? (
                        <div className="row g-4">
                            {filteredResources.map((resource) => {
                                    const fileIcon = getFileTypeIcon(resource.file_type);
                                    return (
                                        <div key={resource.id} className="col-lg-4 col-md-6">
                                            <div className="card h-100 border-0 shadow-sm hover-shadow">
                                                <div className="card-body">
                                                    <div className="d-flex align-items-start mb-3">
                                                        <div 
                                                            className="avatar-sm rounded flex-shrink-0 d-flex align-items-center justify-content-center me-3"
                                                            style={{ backgroundColor: `${fileIcon.color}15` }}
                                                        >
                                                            <i className={`bx ${fileIcon.icon}`} style={{ color: fileIcon.color, fontSize: '24px' }}></i>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1 line-clamp-2">{resource.title}</h6>
                                                            <small className="text-muted">
                                                                <i className={`bx ${resource.category_icon} me-1`}></i>
                                                                {resource.category_name}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    
                                                    <p className="text-muted small mb-3 line-clamp-2">{resource.summary}</p>
                                                    
                                                    <div className="row g-2 mb-3 text-center">
                                                        <div className="col-4">
                                                            <div className="small">
                                                                <i className="bx bx-download text-primary"></i>
                                                                <div className="fw-medium">{resource.download_count}</div>
                                                                <div className="text-muted tiny">Downloads</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="small">
                                                                <i className="bx bx-show text-success"></i>
                                                                <div className="fw-medium">{resource.view_count}</div>
                                                                <div className="text-muted tiny">Views</div>
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <div className="small">
                                                                <i className="bx bx-star text-warning"></i>
                                                                <div className="fw-medium">{resource.average_rating.toFixed(1)}</div>
                                                                <div className="text-muted tiny">Rating</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <small className="text-muted">
                                                            By {resource.author} • {resource.formatted_file_size}
                                                        </small>
                                                        <span className="badge bg-light text-dark">v{resource.version}</span>
                                                    </div>
                                                    
                                                    {renderStars(resource.average_rating, (rating) => rateResource(resource.id, rating))}
                                                </div>
                                                
                                                <div className="card-footer bg-transparent border-0 pt-0">
                                                    <div className="d-grid gap-2">
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={() => downloadResource(resource)}
                                                            disabled={!resource.can_download}
                                                        >
                                                            <i className="bx bx-download me-2"></i>
                                                            Download
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="bx bx-download display-1 text-muted"></i>
                            <h5 className="mt-3 text-muted">No Resources Found</h5>
                            <p className="text-muted">
                                {searchTerm 
                                    ? `No resources match your search criteria`
                                    : `No downloadable resources are currently available.`
                                }
                            </p>
                            {searchTerm && (
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={() => setSearchTerm('')}
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Infinite Scroll Loading Indicator */}
            {loading && resources.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading more...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading more resources...</p>
                    </div>
                </div>
            )}

            {/* Resources Count */}
            {resources.length > 0 && (
                <div className="row mt-4">
                    <div className="col-12 text-center text-muted">
                        Showing {filteredResources.length} of {totalCount} resources
                        {hasMore && !loading && (
                            <div className="mt-2">
                                <small>Scroll down to load more</small>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .hover-shadow {
                    transition: box-shadow 0.15s ease-in-out;
                }
                
                .hover-shadow:hover {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }
                
                .cursor-pointer {
                    cursor: pointer;
                }
                
                .tiny {
                    font-size: 0.7rem;
                }
                
                .avatar-sm {
                    width: 3rem;
                    height: 3rem;
                }
            `}</style>
        </div>
    );
};

export default DownloadsCenter;
